
import React, { useState, useEffect, useRef } from "react";

const API = process.env.REACT_APP_API; // Change this to your backend URL if different

// --- NEW REUSABLE SEARCHABLE DROPDOWN COMPONENT ---
// --- NEW REUSABLE SEARCHABLE DROPDOWN COMPONENT ---
const SearchableSelect = ({ options, value, onChange, placeholder, disabled }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const wrapperRef = useRef(null);

  // Sync the input text with the selected value when it changes
  useEffect(() => {
    const selectedOption = options.find((o) => o.id === value);
    setSearchTerm(selectedOption ? selectedOption.name : "");
  }, [value, options]);

  // Handle clicking outside to close the dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
        const selectedOption = options.find((o) => o.id === value);
        setSearchTerm(selectedOption ? selectedOption.name : "");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [value, options]);

  const filteredOptions = options.filter((option) =>
    option.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div ref={wrapperRef} style={{ position: "relative", width: "100%" }}>
      <input
        className="select-input"
        type="text"
        value={searchTerm}
        onChange={(e) => {
          setSearchTerm(e.target.value);
          setIsOpen(true);
        }}
        onFocus={() => setIsOpen(true)}
        placeholder={placeholder}
        disabled={disabled}
        style={{ width: "100%", boxSizing: "border-box", cursor: disabled ? "not-allowed" : "text" }}
      />
      
      {isOpen && !disabled && (
        <ul
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            width: "100%", // Force it to match the input width
            backgroundColor: "#0f172a", // Dark slate background
            border: "1px solid #334155",
            borderRadius: "8px",
            zIndex: 99999, // Maximum Z-index to float above everything
            maxHeight: "250px",
            overflowY: "auto",
            listStyle: "none",
            padding: "0", // CRITICAL: Removes browser default left-padding
            margin: "8px 0 0 0",
            boxShadow: "0 10px 25px rgba(0, 0, 0, 0.5)",
            textAlign: "left"
          }}
        >
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option) => (
              <li
                key={option.id}
                onMouseDown={() => {
                  onChange(option.id);
                  setIsOpen(false);
                }}
                style={{
                  padding: "12px 16px",
                  cursor: "pointer",
                  color: "#ffffff", // CRITICAL: Forces text to be white
                  fontSize: "0.95rem",
                  borderBottom: "1px solid #1e293b", // Subtle separator line
                  margin: 0,
                  transition: "background 0.2s ease"
                }}
                onMouseEnter={(e) => (e.target.style.backgroundColor = "#1e293b")} 
                onMouseLeave={(e) => (e.target.style.backgroundColor = "transparent")}
              >
                {option.name}
              </li>
            ))
          ) : (
            <li style={{ padding: "12px 16px", color: "#94a3b8", fontStyle: "italic", margin: 0 }}>
              No matches found
            </li>
          )}
        </ul>
      )}
    </div>
  );
};

// --- YOUR MAIN FILTERS COMPONENT ---
const Filters = ({ onSelect, isSubmitting = false }) => {
  const [projects, setProjects] = useState([]);
  const [sprints, setSprints] = useState([]);
  
  const [selectedProject, setSelectedProject] = useState("");
  const [selectedSprint, setSelectedSprint] = useState("");
  const [isLoadingProjects, setIsLoadingProjects] = useState(true);

  useEffect(() => {
    const loadProjects = async () => {
      try {
        const res = await fetch(`${API}/api/projects`, { cache: "no-store" }); 
        const data = await res.json();
        
        if (Array.isArray(data)) {
          setProjects(data);
        } else {
          setProjects([]);
        }
        setIsLoadingProjects(false);
      } catch (error) {
        console.error("Failed to load projects", error);
        setProjects([]);
        setIsLoadingProjects(false);
      }
    };
    loadProjects();
  }, []);

  // Updated to accept the direct projectId from the SearchableSelect
  const handleProjectChange = async (projectId) => {
    setSelectedProject(projectId);
    setSelectedSprint("");
    setSprints([]);

    if (projectId) {
      try {
        const res = await fetch(`${API}/api/projects/${projectId}/sprints`, { cache: "no-store" }); 
        const data = await res.json();
        
        if (Array.isArray(data)) {
          setSprints(data);
        } else {
          setSprints([]);
        }
      } catch (error) {
        console.error("Failed to load sprints", error);
      }
    }
  };

  const handleSubmit = () => {
    if (selectedProject && selectedSprint) {
      onSelect(selectedProject, selectedSprint);
    } else {
      alert("Please select both a project and a sprint!");
    }
  };

  return (
    <div className="filter-bar">
      <div className="filter-group">
        <label className="filter-label">Project</label>
        <SearchableSelect
          options={projects}
          value={selectedProject}
          onChange={handleProjectChange}
          placeholder={isLoadingProjects ? "Loading Projects..." : "Type to search Project..."}
          disabled={isLoadingProjects}
        />
      </div>

      <div className="filter-group">
        <label className="filter-label">Sprint</label>
        <SearchableSelect
          options={sprints}
          value={selectedSprint}
          onChange={setSelectedSprint} // Updates sprint state directly
          placeholder={
            !selectedProject
              ? "Select Project first"
              : sprints.length === 0
              ? "No Sprints found"
              : "Type to search Sprint..."
          }
          disabled={!selectedProject || sprints.length === 0}
        />
      </div>

      <button
        className="btn-primary"
        onClick={handleSubmit}
        disabled={!selectedProject || !selectedSprint || isSubmitting}
      >
        <span className="btn-primary__content">
          {isSubmitting && <span className="btn-primary__spinner" aria-hidden="true" />}
          <span>{isSubmitting ? "Loading Dashboard" : "Load Dashboard"}</span>
        </span>
        <small>{isSubmitting ? "Fetching sprint analytics" : "Reveal the sprint board"}</small>
      </button>
    </div>
  );
};

export default Filters;
